/**
 * Chat Message Component - Ultra Minimalist Design
 * 
 * World-class features:
 * - Clean, minimalist typography inspired by Notion/Linear
 * - Subtle, elegant quote blocks without heavy colors
 * - Beautiful section headings with delicate styling
 * - Premium spacing and visual hierarchy
 * - No blue accents - pure monochrome elegance
 */
import React, { useMemo, useState, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Zap, ExternalLink, ArrowRight } from 'lucide-react';
import type { ChatMessage as ChatMessageType, CaseSummary, Citation } from '../types';
import { CitationBadge, DocumentPreviewModal } from '../../../ai/CitationComponents';

interface ChatMessageProps {
  msg: ChatMessageType;
  idx: number;
  user: any;
  briefCollapsed: boolean;
  setBriefCollapsed: (collapsed: boolean) => void;
  chatHistoryLength: number;
  priorityCases: CaseSummary[];
  onNavigate: (url: string) => void;
  onSuggestionClick: (suggestion: string) => void;
  t: (en: string, no: string) => string;
  isStreaming?: boolean;
  onOpenDocumentSidePanel?: (citation: Citation) => void;
  language?: string;
}

// ChatGPT-style markdown renderer with world-class typography
const renderMarkdown = (
  text: string, 
  cases: CaseSummary[], 
  onNavigate: (url: string) => void,
  citations?: Citation[],
  onOpenDocument?: (documentId: string, chunkId?: string) => void,
  language: string = 'nb'
): React.ReactNode => {
  if (!text) return null;
  
  // Localized strings
  const viewInDoc = language === 'en' ? 'View in document' : 'Se i dokument';
  const viewSource = language === 'en' ? 'View source' : 'Se kilde';
  
  const cleanText = text.replace(/\[ACTION:[^\]]+\]/g, '').trim();
  if (!cleanText) return null;
  
  const elements: React.ReactNode[] = [];
  const lines = cleanText.split('\n');
  let currentList: { type: 'ol' | 'ul'; items: React.ReactNode[]; startNum?: number } | null = null;
  let listKey = 0;
  
  const citationMap = citations ? new Map(citations.map(c => [c.id, c])) : new Map();
  
  // Process inline formatting
  const processInline = (line: string, keyPrefix: string): React.ReactNode => {
    const parts: React.ReactNode[] = [];
    let remaining = line;
    let partIdx = 0;
    
    while (remaining.length > 0) {
      const citationMatch = remaining.match(/\[cite_(\d+)\]|\[(\d+)\]/);
      const boldMatch = remaining.match(/\*\*([^*]+)\*\*/);
      
      let firstMatch: { type: string; match: RegExpMatchArray; index: number } | null = null;
      
      if (citationMatch?.index !== undefined) {
        firstMatch = { type: 'citation', match: citationMatch, index: citationMatch.index };
      }
      if (boldMatch?.index !== undefined && (!firstMatch || boldMatch.index < firstMatch.index)) {
        firstMatch = { type: 'bold', match: boldMatch, index: boldMatch.index };
      }
      
      if (firstMatch) {
        if (firstMatch.index > 0) {
          parts.push(<span key={`${keyPrefix}-${partIdx++}`}>{remaining.slice(0, firstMatch.index)}</span>);
        }
        
        if (firstMatch.type === 'citation') {
          const citNum = firstMatch.match[1] || firstMatch.match[2];
          const citationId = `cite_${citNum}`;
          const citation = citationMap.get(citationId);
          
          if (citation && onOpenDocument) {
            parts.push(
              <CitationBadge
                key={`${keyPrefix}-cite-${partIdx++}`}
                citation={citation}
                onOpenDocument={onOpenDocument}
              />
            );
          } else {
            // Fallback - still make it clickable with console log for debugging
            parts.push(
              <button
                key={`${keyPrefix}-cite-${partIdx++}`}
                onClick={() => {
                  console.log('🔍 Citation clicked (no citation found):', citNum);
                  console.log('🔍 Available citations:', Array.from(citationMap.keys()));
                }}
                className="inline-flex items-center justify-center w-5 h-5 ml-0.5 mr-0.5 text-[10px] font-medium text-gray-500 bg-gray-100 hover:bg-amber-100 hover:text-amber-700 rounded-full transition-all duration-150 cursor-pointer align-super"
                title="Kilde ikke funnet"
              >
                {citNum}
              </button>
            );
          }
          remaining = remaining.slice(firstMatch.index + firstMatch.match[0].length);
        } else if (firstMatch.type === 'bold') {
          const boldText = firstMatch.match[1];
          const matchedCase = cases.find(c => 
            (c.title && c.title.toLowerCase().includes(boldText.toLowerCase())) ||
            (c.client && c.client.toLowerCase().includes(boldText.toLowerCase()))
          );
          
          if (matchedCase) {
            parts.push(
              <button
                key={`${keyPrefix}-case-${partIdx++}`}
                onClick={() => onNavigate(`/lexy/case/${matchedCase.id}/workspace`)}
                className="inline-flex items-center gap-1 font-semibold text-gray-900 hover:text-gray-600 transition-colors duration-150"
              >
                <span className="border-b border-gray-300 hover:border-gray-500">{boldText}</span>
                <ExternalLink className="w-3 h-3 opacity-40" />
              </button>
            );
          } else {
            parts.push(
              <strong key={`${keyPrefix}-bold-${partIdx++}`} className="font-semibold text-gray-900">
                {boldText}
              </strong>
            );
          }
          remaining = remaining.slice(firstMatch.index + firstMatch.match[0].length);
        }
      } else {
        if (remaining) {
          parts.push(<span key={`${keyPrefix}-${partIdx++}`}>{remaining}</span>);
        }
        break;
      }
    }
    
    return parts.length > 0 ? <>{parts}</> : line;
  };
  
  // World-class list rendering with proper indent and darker bullets
  const flushList = () => {
    if (currentList) {
      if (currentList.type === 'ol') {
        elements.push(
          <ol key={`list-${listKey++}`} className="my-7 ml-6 space-y-4">
            {currentList.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-800 pl-1">
                <span className="flex-shrink-0 text-gray-600 font-medium min-w-[1.5rem] text-[15px]">
                  {(currentList?.startNum || 1) + i}.
                </span>
                <span className="flex-1 leading-[1.9] text-[15px]">{item}</span>
              </li>
            ))}
          </ol>
        );
      } else {
        elements.push(
          <ul key={`list-${listKey++}`} className="my-7 ml-6 space-y-4">
            {currentList.items.map((item, i) => (
              <li key={i} className="flex items-start gap-3 text-gray-800 pl-1">
                <span className="flex-shrink-0 w-[5px] h-[5px] bg-gray-700 rounded-full mt-[11px]" />
                <span className="flex-1 leading-[1.9] text-[15px]">{item}</span>
              </li>
            ))}
          </ul>
        );
      }
      currentList = null;
    }
  };
  
  lines.forEach((line, idx) => {
    const trimmed = line.trim();
    
    if (!trimmed) {
      flushList();
      return;
    }

    // SAGA-STYLE Quote with citation: «text» [cite_1] - Beautiful gray background box
    // Also matches lines that START with a quote followed by citation
    const quoteWithCiteMatch = trimmed.match(/^[«"](.+?)[»"]\s*\[(?:cite_)?(\d+)\]\.?$/);
    if (quoteWithCiteMatch) {
      flushList();
      const quoteText = quoteWithCiteMatch[1];
      const citNum = quoteWithCiteMatch[2];
      
      elements.push(
        <div key={`quote-${idx}`} className="my-6">
          <div className="relative bg-gradient-to-r from-gray-50 to-gray-50/50 border-l-[3px] border-amber-400 rounded-r-lg px-5 py-4 shadow-sm">
            <p className="text-gray-700 text-[15px] leading-[1.8] italic">
              "{quoteText}"
            </p>
            <button 
              onClick={() => {
                const citation = citationMap.get(`cite_${citNum}`);
                if (citation && onOpenDocument) {
                  onOpenDocument(citation.documentId, citation.chunkId);
                }
              }}
              className="inline-flex items-center gap-2 mt-3 text-[13px] text-gray-500 hover:text-amber-600 transition-colors cursor-pointer group"
            >
              <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[11px] font-semibold group-hover:bg-amber-200 transition-colors">{citNum}</span>
              <span className="group-hover:underline">{viewInDoc}</span>
              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      );
      return;
    }
    
    // SAGA-STYLE: Quote block that's part of a paragraph - "Avtalen sier: «text» [cite_1]"
    const inlineCiteQuoteMatch = trimmed.match(/^(.+?)[:\s]+[«"](.+?)[»"]\s*\[(?:cite_)?(\d+)\]\.?$/);
    if (inlineCiteQuoteMatch) {
      flushList();
      const prefix = inlineCiteQuoteMatch[1];
      const quoteText = inlineCiteQuoteMatch[2];
      const citNum = inlineCiteQuoteMatch[3];
      
      elements.push(
        <div key={`iq-${idx}`} className="my-6">
          <p className="text-gray-800 text-[15px] leading-[1.8] mb-3">
            {processInline(prefix, `iq-pre-${idx}`)}:
          </p>
          <div className="relative bg-gradient-to-r from-gray-50 to-gray-50/50 border-l-[3px] border-amber-400 rounded-r-lg px-5 py-4 shadow-sm">
            <p className="text-gray-700 text-[15px] leading-[1.8] italic">
              "{quoteText}"
            </p>
            <button 
              onClick={() => {
                const citation = citationMap.get(`cite_${citNum}`);
                if (citation && onOpenDocument) {
                  onOpenDocument(citation.documentId, citation.chunkId);
                }
              }}
              className="inline-flex items-center gap-2 mt-3 text-[13px] text-gray-500 hover:text-amber-600 transition-colors cursor-pointer group"
            >
              <span className="w-5 h-5 bg-amber-100 text-amber-700 rounded-full flex items-center justify-center text-[11px] font-semibold group-hover:bg-amber-200 transition-colors">{citNum}</span>
              <span className="group-hover:underline">{viewInDoc}</span>
              <ArrowRight className="w-3 h-3 opacity-0 group-hover:opacity-100 transition-opacity" />
            </button>
          </div>
        </div>
      );
      return;
    }
    
    // SAGA-STYLE: Detect standalone quotes without explicit [cite] but with text in «» or ""
    const standaloneQuoteMatch = trimmed.match(/^[«"](.{20,})[»"]\.?$/);
    if (standaloneQuoteMatch) {
      flushList();
      const quoteText = standaloneQuoteMatch[1];
      
      elements.push(
        <div key={`sq-${idx}`} className="my-6">
          <div className="relative bg-gradient-to-r from-gray-50 to-gray-50/50 border-l-[3px] border-gray-300 rounded-r-lg px-5 py-4">
            <p className="text-gray-700 text-[15px] leading-[1.8] italic">
              "{quoteText}"
            </p>
          </div>
        </div>
      );
      return;
    }

    // SAGA-STYLE main section header: "1. Nature and Purpose of the Agreement"
    const mainSectionMatch = trimmed.match(/^\*?\*?(\d+)\.\s+([A-ZÆØÅa-z][^*]+?)\*?\*?$/);
    if (mainSectionMatch) {
      flushList();
      elements.push(
        <div key={`main-${idx}`} className="mt-14 mb-8">
          <h2 className="text-[22px] font-semibold text-gray-900 tracking-tight leading-tight">
            {mainSectionMatch[1]}. {mainSectionMatch[2].trim()}
          </h2>
          <div className="mt-5 h-[1px] bg-gray-200" />
        </div>
      );
      return;
    }

    // Callout: "Why this matters:" or "In short:" - Saga style with background
    const calloutMatch = trimmed.match(/^\*?\*?(Why this matters|In short|Hvorfor dette er viktig|Kort sagt|Konsekvens|Purpose and Scope|Formål og omfang):\*?\*?\s*(.*)$/i);
    if (calloutMatch) {
      flushList();
      elements.push(
        <div key={`callout-${idx}`} className="my-8 bg-amber-50/80 border-l-[3px] border-amber-400 rounded-r-md px-5 py-4">
          <p className="text-[15px] text-gray-800 leading-[1.9]">
            <strong className="font-semibold text-gray-900">{calloutMatch[1]}:</strong>
            {calloutMatch[2] && <span className="ml-1.5">{processInline(calloutMatch[2], `callout-${idx}`)}</span>}
          </p>
        </div>
      );
      return;
    }

    // SAGA-STYLE Sub-section: "2.1 Transfer Restrictions / Right of First Refusal (ROFR)"
    const subSectionMatch = trimmed.match(/^\*?\*?(\d+\.\d+)\s+([A-ZÆØÅa-zæøå][^*]+?)\*?\*?$/);
    if (subSectionMatch) {
      flushList();
      elements.push(
        <h4 key={`sub-${idx}`} className="text-[18px] font-semibold text-gray-900 mt-12 mb-5">
          {subSectionMatch[1]} {subSectionMatch[2].trim()}
        </h4>
      );
      return;
    }
    
    // Bold header: "**Parties:**" or "**Purpose:**" - Should NOT be a bullet item
    const boldHeaderMatch = trimmed.match(/^\*\*([^*]+?):?\*\*:?\s*$/);
    if (boldHeaderMatch) {
      flushList();
      const headerText = boldHeaderMatch[1].replace(/:+$/, '');
      elements.push(
        <p key={`bh-${idx}`} className="text-[16px] font-semibold text-gray-900 mt-8 mb-2">
          {headerText}
        </p>
      );
      return;
    }
    
    // Bold text followed by colon at start of line (like "**Parties Involved:**") - NOT a bullet
    const boldWithColonMatch = trimmed.match(/^\*\*([^*:]+):?\*\*\s*$/);
    if (boldWithColonMatch) {
      flushList();
      elements.push(
        <p key={`bwc-${idx}`} className="text-[16px] font-semibold text-gray-900 mt-8 mb-2">
          {boldWithColonMatch[1]}
        </p>
      );
      return;
    }

    // Headers with #
    const headerMatch = trimmed.match(/^(#{1,3})\s+(.+)$/);
    if (headerMatch) {
      flushList();
      const level = headerMatch[1].length;
      const headerText = processInline(headerMatch[2], `h-${idx}`);
      if (level === 1) {
        elements.push(
          <div key={`h1-${idx}`} className="mt-14 mb-8">
            <h2 className="text-[22px] font-semibold text-gray-900 tracking-tight">{headerText}</h2>
            <div className="mt-5 h-[1px] bg-gray-200" />
          </div>
        );
      } else if (level === 2) {
        elements.push(
          <h3 key={`h2-${idx}`} className="text-[20px] font-semibold text-gray-900 mt-12 mb-5">{headerText}</h3>
        );
      } else {
        elements.push(
          <h4 key={`h3-${idx}`} className="text-[17px] font-semibold text-gray-900 mt-10 mb-4">{headerText}</h4>
        );
      }
      return;
    }
    
    // Numbered list
    const numberedMatch = trimmed.match(/^(\d+)[.)]\s+(.+)$/);
    if (numberedMatch) {
      const num = parseInt(numberedMatch[1]);
      if (currentList?.type !== 'ol') {
        flushList();
        currentList = { type: 'ol', items: [], startNum: num };
      }
      currentList.items.push(processInline(numberedMatch[2], `li-${idx}`));
      return;
    }
    
    // Bullet list - but check if it's a bold sub-header first
    const bulletMatch = trimmed.match(/^[-•*]\s+(.+)$/);
    if (bulletMatch) {
      const bulletContent = bulletMatch[1];
      
      // Check if this bullet is just a bold header like "- **Parties Involved:**"
      const boldSubHeaderMatch = bulletContent.match(/^\*\*([^*]+?):?\*\*:?\s*$/);
      if (boldSubHeaderMatch) {
        // Treat as sub-header, not bullet
        flushList();
        elements.push(
          <p key={`bsh-${idx}`} className="text-[15px] font-semibold text-gray-800 mt-6 mb-2">
            {boldSubHeaderMatch[1].replace(/:+$/, '')}
          </p>
        );
        return;
      }
      
      if (currentList?.type !== 'ul') {
        flushList();
        currentList = { type: 'ul', items: [] };
      }
      currentList.items.push(processInline(bulletContent, `li-${idx}`));
      return;
    }
    
    // Regular paragraph - World-class typography with generous spacing
    flushList();
    elements.push(
      <p key={`p-${idx}`} className="text-gray-800 leading-[1.9] my-6 text-[15px]">
        {processInline(trimmed, `p-${idx}`)}
      </p>
    );
  });
  
  flushList();
  
  return <div className="space-y-3">{elements}</div>;
};

export const ChatMessage: React.FC<ChatMessageProps> = ({
  msg,
  idx,
  user,
  briefCollapsed,
  setBriefCollapsed,
  chatHistoryLength,
  priorityCases,
  onNavigate,
  onSuggestionClick,
  t,
  isStreaming = false,
  onOpenDocumentSidePanel,
  language = 'nb',
}) => {
  const [previewModal, setPreviewModal] = useState<{ documentId: string; chunkId?: string } | null>(null);
  
  const handleOpenDocument = useCallback((documentId: string, chunkId?: string) => {
    console.log('🔍 Opening document:', { documentId, chunkId });
    const citation = msg.citations?.find(c => c.documentId === documentId && c.chunkId === chunkId);
    console.log('🔍 Found citation:', citation);
    
    if (onOpenDocumentSidePanel && citation) {
      onOpenDocumentSidePanel(citation);
    } else {
      setPreviewModal({ documentId, chunkId });
    }
  }, [msg.citations, onOpenDocumentSidePanel]);
  
  const renderedContent = useMemo(() => {
    if (msg.type === 'assistant') {
      return renderMarkdown(
        msg.text, 
        priorityCases, 
        onNavigate, 
        msg.citations, 
        handleOpenDocument,
        language
      );
    }
    return msg.text;
  }, [msg.text, msg.type, priorityCases, onNavigate, msg.citations, handleOpenDocument, language]);

  return (
    <motion.div
      key={idx}
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.2, ease: 'easeOut' }}
      className="py-4"
    >
      {msg.type === 'user' ? (
        /* User Message - Minimalist bubble */
        <div className="flex items-start gap-3 justify-end">
          <div className="max-w-[75%] bg-gray-100 rounded-2xl rounded-tr-sm px-4 py-3">
            <p className="text-gray-800 leading-relaxed text-[15px]">{msg.text}</p>
          </div>
          <div className="w-8 h-8 bg-gray-800 rounded-full flex items-center justify-center shrink-0 text-white text-xs font-medium">
            {user?.name?.charAt(0)?.toUpperCase() || 'U'}
          </div>
        </div>
      ) : msg.type === 'brief' ? null : msg.type === 'assistant' ? (
        /* AI Message - Ultra minimalist */
        <div className="flex items-start gap-4">
          {/* AI Avatar - Lexy logo (same as sidebar) */}
          <div className="relative">
            {isStreaming && !msg.text && (
              <div className="absolute -inset-1 bg-purple-300 rounded-lg opacity-40 animate-ping" />
            )}
            <div className="relative w-8 h-8 bg-gradient-to-r from-violet-600 to-purple-600 rounded-lg flex items-center justify-center shrink-0">
              <Zap className={`w-4 h-4 text-white ${isStreaming && !msg.text ? 'animate-pulse' : ''}`} />
            </div>
          </div>
          
          {/* Message Content - Clean typography */}
          <div className="flex-1 min-w-0 pt-0.5">
            {/* Thinking indicator - Simple dots */}
            {isStreaming && !msg.text && (
              <div className="flex items-center gap-1 py-2">
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                <span className="w-1.5 h-1.5 bg-violet-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
              </div>
            )}
            
            {/* Response content */}
            {msg.text && (
              <div className="text-[15px]">
                {renderedContent}
                {isStreaming && (
                  <span className="inline-block w-0.5 h-4 bg-gray-400 ml-1 animate-pulse rounded-full align-middle" />
                )}
              </div>
            )}
            
            {/* Action buttons - Minimalist style */}
            {msg.actions && msg.actions.length > 0 && !isStreaming && (
              <div className="flex flex-wrap gap-2 mt-5">
                {msg.actions.map((action, i) => (
                  <button
                    key={i}
                    onClick={() => {
                      if (action.action === 'open_case' && action.caseId) {
                        onNavigate(`/lexy/case/${action.caseId}/workspace`);
                      } else if (action.action === 'navigate' && action.url) {
                        onNavigate(action.url);
                      }
                    }}
                    className={`group px-4 py-2.5 rounded-lg text-sm font-medium transition-all duration-150 flex items-center gap-2 ${
                      action.primary 
                        ? 'bg-gray-900 hover:bg-gray-800 text-white' 
                        : 'bg-gray-100 hover:bg-gray-200 text-gray-700'
                    }`}
                  >
                    <span>{action.label}</span>
                    <ArrowRight className="w-3.5 h-3.5 transition-transform group-hover:translate-x-0.5" />
                  </button>
                ))}
              </div>
            )}

            {/* Suggestion chips */}
            {msg.suggestions && msg.suggestions.length > 0 && !isStreaming && !msg.actions && (
              <div className="flex flex-wrap gap-2 mt-5 pt-4 border-t border-gray-100">
                {msg.suggestions.map((suggestion, i) => (
                  <button
                    key={i}
                    onClick={() => onSuggestionClick(suggestion)}
                    className="px-3 py-2 bg-gray-50 hover:bg-gray-100 text-gray-600 hover:text-gray-800 text-sm rounded-lg transition-colors duration-150"
                  >
                    {suggestion} →
                  </button>
                ))}
              </div>
            )}
          </div>
        </div>
      ) : null}
      
      {previewModal && (
        <DocumentPreviewModal
          isOpen={true}
          onClose={() => setPreviewModal(null)}
          documentId={previewModal.documentId}
          chunkId={previewModal.chunkId}
        />
      )}
    </motion.div>
  );
};

export default ChatMessage;
